import { useContext, useState } from 'react';
import { Link, useHistory, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Arrow from '../../assets/Arrow';
import CxbLogo from '../../assets/CxbLogo';
import SearchIcon from '../../assets/SearchIcon';
import SellButton from '../../assets/SellButton';
import SellButtonPlus from '../../assets/SellButtonPlus';
import BuyButton from '../../assets/BuyButton';
import BuyButtonPlus from '../../assets/BuyButtonPlus';
import CategoryButton from '../../assets/CategoryButton';
import { AuthContext } from '../../contextStore/AuthContext';
import { useSearchFilter } from '../../hooks/useSearchFilter';
import NotificationBell from '../Notifications/NotificationBell';
import CreateBuyOfferModal from '../Offers/CreateBuyOfferModal';
import './Header.css';
import LanguageSelector from '../LanguageSelector';
import LocationDropdown from './LocationDropdown';
import MobileMenu from './MobileMenu';
import UserDropdown from './UserDropdown';

// Quick-create buttons pinned next to Sell/Buy: open /create with the
// category preselected via router state (matched by name in useCreateForm).
const CREATE_CATEGORY_BUTTONS = [
  { key: 'investment', category: 'Investment' },
  { key: 'services', category: 'Services' },
  { key: 'bonuses', category: 'Bonuses' },
];

function Header() {
  const { t } = useTranslation();
  const history = useHistory();
  const location = useLocation();
  const { user } = useContext(AuthContext);
  const loginState = { from: { pathname: location.pathname } };
  const {
    filteredData,
    wordEntered,
    isOpen,
    wrapperRef,
    handleFilter,
    clearInput,
    closeDropdown,
    handleSelectedSearch,
    handleKeyDown,
  } = useSearchFilter();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [showBuyModal, setShowBuyModal] = useState(false);

  const handleBuyModalClose = () => {
    setShowBuyModal(false);
  };

  const handleBuyModalSuccess = () => {
    setShowBuyModal(false);
  };

  const handleSearchSubmit = () => {
    if (wordEntered.trim()) {
      history.push(`/search?q=${encodeURIComponent(wordEntered.trim())}`);
    } else {
      history.push('/search');
    }
    closeDropdown();
  };

  const handleEmptyClick = () => {
    history.push('/search');
  };

  return (
    <div className="headerParentDiv">
      <div className="headerChildDiv">
        <button
          type="button"
          className="headerHamburger"
          onClick={() => setMobileMenuOpen(true)}
          aria-label={t('header.openMenu')}
        >
          <span />
          <span />
          <span />
        </button>
        <Link to="/" className="brandName">
          <CxbLogo />
        </Link>
        <div className="headerLocationWrap">
          <LocationDropdown />
        </div>
        <div className="headerSearchWrap" ref={wrapperRef}>
          <div className="headerSearchInner">
            <input
              type="text"
              placeholder={t('header.searchPlaceholder')}
              value={wordEntered}
              onChange={handleFilter}
              onKeyDown={(e) => {
                handleKeyDown(e);
                if (e.key === 'Enter') handleSearchSubmit();
              }}
            />
            {wordEntered.trim() && (
              <button
                type="button"
                className="headerClearBtn"
                onClick={clearInput}
                aria-label={t('header.clearSearch')}
              >
                &times;
              </button>
            )}
            <button
              type="button"
              className="headerSearchBtn"
              onClick={
                wordEntered.trim() ? handleSearchSubmit : handleEmptyClick
              }
              aria-label={t('common.search')}
            >
              <SearchIcon />
            </button>
          </div>
          {isOpen && filteredData.length > 0 && (
            <div className="dataResult-header">
              {filteredData.slice(0, 15).map((value, key) => (
                <div
                  key={value.id || key}
                  className="dataItem-header"
                  role="button"
                  tabIndex={0}
                  onClick={() => handleSelectedSearch(value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      handleSelectedSearch(value);
                    }
                  }}
                >
                  <p>{value.name}</p>
                </div>
              ))}
            </div>
          )}
        </div>
        <LanguageSelector />
        <div className="headerRight">
          {user ? (
            <>
              <NotificationBell />
              <UserDropdown />
            </>
          ) : (
            <Link
              to={{ pathname: '/login', state: loginState }}
              className="headerLoginLink"
            >
              {t('header.login')}
            </Link>
          )}
        </div>
        <Link to="/create" className="headerSellLink">
          <div className="sellMenu">
            <SellButton />
            <div className="sellMenuContent">
              <SellButtonPlus />
              <span>{t('header.sell')}</span>
            </div>
          </div>
        </Link>
        <button
          type="button"
          className="headerBuyLink"
          onClick={() => setShowBuyModal(true)}
        >
          <div className="buyMenu">
            <BuyButton />
            <div className="buyMenuContent">
              <BuyButtonPlus />
              <span>{t('header.buy')}</span>
            </div>
          </div>
        </button>
        {CREATE_CATEGORY_BUTTONS.map(({ key, category }) => (
          <button
            type="button"
            key={key}
            className="headerCategoryLink"
            onClick={() => history.push('/create', { category })}
          >
            <div className={`categoryMenu categoryMenu--${key}`}>
              <CategoryButton variant={key} />
              <div className="categoryMenuContent">
                <span>{t(`header.createCategory.${key}`)}</span>
              </div>
            </div>
          </button>
        ))}
        {showBuyModal && (
          <CreateBuyOfferModal
            onClose={handleBuyModalClose}
            onSuccess={handleBuyModalSuccess}
          />
        )}
      </div>
      <MobileMenu
        open={mobileMenuOpen}
        onClose={() => setMobileMenuOpen(false)}
      />
    </div>
  );
}

export default Header;
